// src/pages/AgendasViaticos.jsx
import axios from "axios";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const AgendasViaticos = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isEditMode = location.pathname.includes("/editar/");
  const isNewMode = location.pathname.includes("/nuevo/");

  const [activeTab, setActiveTab] = useState("info");
  const [loading, setLoading] = useState(false);

  const [agendaId, setAgendaId] = useState("");
  const [contratoId, setContratoId] = useState("");
  const [contrato, setContrato] = useState(null);

  const [regionales, setRegionales] = useState([]);
  const [centros, setCentros] = useState([]);

  const [direccionGeneralRegional, setDireccionGeneralRegional] = useState("");
  const [dependenciaCentro, setDependenciaCentro] = useState("");
  const [ciudadDepartamentoMunicipio, setCiudadDepartamentoMunicipio] =
    useState("");
  const [contactoNombre, setContactoNombre] = useState("");
  const [contactoEmail, setContactoEmail] = useState("");

  const [ruta, setRuta] = useState("");
  const [entidad, setEntidad] = useState("");
  const [fechaInicioDesplazamiento, setFechaInicioDesplazamiento] =
    useState("");
  const [fechaFinDesplazamiento, setFechaFinDesplazamiento] = useState("");
  const [objetivoDesplazamiento, setObjetivoDesplazamiento] = useState("");

  const supervisorNombre = useMemo(
    () => contrato?.supervisor?.nombre || "—",
    [contrato]
  );

  const supervisorCargo = useMemo(
    () => contrato?.supervisor?.cargo || "—",
    [contrato]
  );

  const ordenadorNombre = useMemo(
    () => contrato?.ordenGasto?.nombre || contrato?.subdirector?.nombre || "—",
    [contrato]
  );

  const ordenadorCargo = useMemo(
    () => contrato?.ordenGasto?.cargo || contrato?.subdirector?.cargo || "—",
    [contrato]
  );

  const [gastoAereo, setGastoAereo] = useState("");
  const [gastoTerrestre, setGastoTerrestre] = useState("");
  const [gastoIntermunicipal, setGastoIntermunicipal] = useState("");

  const blankActividad = useMemo(
    () => ({ horaInicio: "", horaFin: "", actividad: "" }),
    []
  );

  const blankDia = useMemo(
    () => ({
      fecha: "",
      pernocta: false,
      rutaIda: "",
      transporteIda: "",
      actividades: [{ ...blankActividad }],
      rutaRegreso: "",
      transporteRegreso: "",
      observaciones: "",
    }),
    [blankActividad]
  );

  const [agendaDias, setAgendaDias] = useState([{ ...blankDia }]);

  const blankObligacion = useMemo(() => ({ obligacion: "", detalle: "" }), []);
  const [obligacionesDin, setObligacionesDin] = useState([
    { ...blankObligacion },
  ]);

  const [opcionesObligaciones, setOpcionesObligaciones] = useState([]);
  const [loadingObligaciones, setLoadingObligaciones] = useState(false);

  const stepProgress = useMemo(() => {
    if (activeTab === "info") return 33;
    if (activeTab === "agenda") return 66;
    return 100;
  }, [activeTab]);

  const totalGastos = useMemo(() => {
    const a = Number(gastoAereo || 0);
    const t = Number(gastoTerrestre || 0);
    const i = Number(gastoIntermunicipal || 0);
    return a + t + i;
  }, [gastoAereo, gastoTerrestre, gastoIntermunicipal]);

  const formatCOP = (value) => {
    try {
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        maximumFractionDigits: 0,
      }).format(Number(value || 0));
    } catch {
      return String(value || 0);
    }
  };

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const getModuloAgendas = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
          ? JSON.parse(usuarioStorage)
          : userStorage
            ? JSON.parse(userStorage)
            : usuariosStorage
              ? JSON.parse(usuariosStorage)
              : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
        permisosData?.permisos ||
        permisosData?.usuario?.modulos ||
        permisosData?.usuario?.permisos ||
        permisosData?.user?.modulos ||
        permisosData?.user?.permisos ||
        [];

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          const nombreNormalizado = nombre
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "");

          return nombreNormalizado === "agendasdeviaje";
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloAgendas = getModuloAgendas();

  const puedeCrear =
    moduloAgendas.puedeCrear === true || moduloAgendas.crear === true;

  const puedeActualizar =
    moduloAgendas.puedeActualizar === true ||
    moduloAgendas.actualizar === true;

  const tienePermisoFormulario = isEditMode ? puedeActualizar : puedeCrear;

  // ======================================================
  // FECHAS CORREGIDAS
  // Evita que Mongo/UTC corra el día al listar en input type="date"
  // ======================================================
  const toISODate = (dateValue) => {
    if (!dateValue) return "";

    const text = String(dateValue);

    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
      return text;
    }

    if (text.includes("T")) {
      return text.split("T")[0];
    }

    return "";
  };

  const formatDateDisplay = (dateValue) => {
    const iso = toISODate(dateValue);
    if (!iso) return "—";

    const [year, month, day] = iso.split("-");
    return `${day}/${month}/${year}`;
  };

  const getIdValue = (value) => {
    if (!value) return "";
    if (typeof value === "object" && value !== null) return value._id || "";
    return value;
  };

  const contratistaNombre = useMemo(() => {
    const inst = contrato?.instructor;
    const nombreCompleto =
      contrato?.instructorNombreCompleto ||
      `${inst?.nombre1Instructor || ""} ${inst?.nombre2Instructor || ""} ${inst?.apellido1Instructor || ""
        } ${inst?.apellido2Instructor || ""}`.trim();

    return nombreCompleto || "—";
  }, [contrato]);

  const contratistaDocumento = useMemo(
    () => contrato?.instructor?.documentoInstructor || "—",
    [contrato]
  );

  const contratoNumero = useMemo(() => {
    return (
      contrato?.contrato ||
      contrato?.codigoContrato ||
      contrato?.numeroContrato ||
      contrato?._id ||
      "—"
    );
  }, [contrato]);

  const contratoObjeto = useMemo(() => {
    return (
      contrato?.objetoContrato ||
      contrato?.objetoContractual ||
      contrato?.objeto ||
      "—"
    );
  }, [contrato]);

  const fechaVencimientoContrato = useMemo(() => {
    const f = contrato?.fechaVencimiento || contrato?.fechaFin;
    return formatDateDisplay(f);
  }, [contrato]);

  useEffect(() => {
    const fetchRegionales = async () => {
      try {
        const res = await axios.get("/api/regionales/listall", authConfig());

        if (res.data?.ok) {
          const orden = (res.data.regionales || []).sort((a, b) =>
            (a?.nombreRegional || "").localeCompare(b?.nombreRegional || "")
          );
          setRegionales(orden);
        } else {
          setRegionales([]);
        }
      } catch {
        setRegionales([]);
      }
    };

    fetchRegionales();
  }, []);

  const fetchCentrosByRegional = useCallback(async (regionalId) => {
    if (!regionalId) {
      setCentros([]);
      return [];
    }

    try {
      const res = await axios.get(
        `/api/centrosdeformacion/listxregional/${regionalId}`,
        authConfig()
      );

      if (res.data?.ok) {
        const list = res.data?.regional?.centros || [];
        setCentros(list);
        return list;
      }

      setCentros([]);
      return [];
    } catch {
      setCentros([]);
      return [];
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);

        if (isEditMode) {
          const resAgenda = await axios.get(
            `/api/agendasviaticos/listid/${id}`,
            authConfig()
          );

          const dataAgenda = resAgenda.data;

          if (!dataAgenda?.ok || !dataAgenda?.agenda) {
            Swal.fire(
              "Atención",
              dataAgenda?.message || "No se pudo cargar la agenda",
              "warning"
            );
            return;
          }

          const agenda = dataAgenda.agenda;
          setAgendaId(agenda._id || "");

          const contratoRef = getIdValue(agenda.contrato);

          if (!contratoRef) {
            Swal.fire("Error", "La agenda no tiene contrato asociado.", "error");
            return;
          }

          setContratoId(contratoRef);

          setRuta(agenda.ruta || "");
          setEntidad(agenda.entidad || "");
          setCiudadDepartamentoMunicipio(
            agenda.ciudadDepartamentoMunicipio || ""
          );

          setFechaInicioDesplazamiento(
            toISODate(agenda.fechaInicioDesplazamiento)
          );
          setFechaFinDesplazamiento(toISODate(agenda.fechaFinDesplazamiento));

          setObjetivoDesplazamiento(agenda.objetivoDesplazamiento || "");

          setGastoAereo(String(agenda.gastosaereo ?? ""));
          setGastoTerrestre(String(agenda.gastosterrestre ?? ""));
          setGastoIntermunicipal(String(agenda.gastosintermunicipal ?? ""));

          if (
            Array.isArray(agenda.obligacionesContractuales) &&
            agenda.obligacionesContractuales.length > 0
          ) {
            setObligacionesDin(
              agenda.obligacionesContractuales.map((o) => {
                const texto = String(o?.obligacion || "").trim();

                if (texto.startsWith("OTRA:")) {
                  return {
                    obligacion: "OTRA (especificar)",
                    detalle: texto.replace("OTRA:", "").trim(),
                  };
                }

                return {
                  obligacion: texto,
                  detalle: "",
                };
              })
            );
          } else {
            setObligacionesDin([{ ...blankObligacion }]);
          }

          if (Array.isArray(agenda.agendaDias) && agenda.agendaDias.length > 0) {
            setAgendaDias(
              agenda.agendaDias.map((dia) => ({
                fecha: toISODate(dia.fecha),
                pernocta: !!dia.pernocta,
                rutaIda: dia.rutaIda || "",
                transporteIda: dia.transporteIda || "",
                rutaRegreso: dia.rutaRegreso || "",
                transporteRegreso: dia.transporteRegreso || "",
                observaciones: dia.observaciones || "",
                actividades:
                  Array.isArray(dia.actividades) && dia.actividades.length > 0
                    ? dia.actividades.map((a) => ({
                      horaInicio: a.horaInicio || "",
                      horaFin: a.horaFin || "",
                      actividad: a.actividad || "",
                    }))
                    : [{ ...blankActividad }],
              }))
            );
          } else {
            setAgendaDias([{ ...blankDia }]);
          }

          const contactoTexto = agenda.contactoEntidad || "";

          if (contactoTexto.includes(" - ")) {
            const [nombre, correo] = contactoTexto.split(" - ");
            setContactoNombre(nombre || "");
            setContactoEmail(correo || "");
          } else {
            setContactoNombre(contactoTexto || "");
            setContactoEmail("");
          }

          const resContrato = await axios.get(
            `/api/contratos/listid/${contratoRef}`,
            authConfig()
          );

          const dataContrato = resContrato.data;

          if (!dataContrato?.ok) {
            Swal.fire(
              "Atención",
              dataContrato?.message || "No se pudo cargar el contrato",
              "warning"
            );
            setContrato(null);
            return;
          }

          const c = dataContrato.contrato || dataContrato.message || null;
          setContrato(c);
        }

        if (isNewMode) {
          setAgendaId("");
          setContratoId(id);

          const resContrato = await axios.get(
            `/api/contratos/listid/${id}`,
            authConfig()
          );

          const dataContrato = resContrato.data;

          if (!dataContrato?.ok) {
            Swal.fire(
              "Atención",
              dataContrato?.message || "No se pudo cargar el contrato",
              "warning"
            );
            setContrato(null);
            return;
          }

          const c = dataContrato.contrato || dataContrato.message || null;
          setContrato(c);

          setRuta("");
          setEntidad("");
          setCiudadDepartamentoMunicipio("");
          setFechaInicioDesplazamiento("");
          setFechaFinDesplazamiento("");
          setObjetivoDesplazamiento("");
          setGastoAereo("");
          setGastoTerrestre("");
          setGastoIntermunicipal("");
          setContactoNombre("");
          setContactoEmail("");
          setAgendaDias([{ ...blankDia }]);
          setObligacionesDin([{ ...blankObligacion }]);
        }
      } catch (err) {
        console.error(err);
        Swal.fire(
          "Error",
          err?.response?.data?.message || "No se pudo cargar la información",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEditMode, isNewMode, blankActividad, blankDia, blankObligacion]);

  useEffect(() => {
    const cargarRegionalCentro = async () => {
      if (!contrato) return;

      const regionalId = getIdValue(contrato?.regional);
      const centroId = getIdValue(contrato?.centroFormacion);

      let regionalName = "";

      if (regionalId && regionales.length) {
        regionalName =
          regionales.find((r) => String(r._id) === String(regionalId))
            ?.nombreRegional || "";
      }

      let centroName = "";

      if (regionalId) {
        const centrosList = await fetchCentrosByRegional(regionalId);
        centroName =
          (centrosList || []).find((x) => String(x._id) === String(centroId))
            ?.nombreCentro || "";
      }

      setDireccionGeneralRegional(regionalName || "");
      setDependenciaCentro(centroName || "");
    };

    cargarRegionalCentro();
  }, [contrato, regionales, fetchCentrosByRegional]);

  useEffect(() => {
    const obtenerObligaciones = async () => {
      if (!contratoId) return;

      try {
        setLoadingObligaciones(true);

        const res = await axios.get(
          `/api/obligaciones/contrato/${contratoId}`,
          authConfig()
        );

        const datos = res.data;
        const ok = datos?.ok ?? datos?.success ?? datos?.status;

        if (!ok) {
          setOpcionesObligaciones(["OTRA (especificar)"]);
          return;
        }

        const arr =
          datos?.obligaciones ||
          datos?.data ||
          datos?.results ||
          datos?.payload ||
          [];

        if (!Array.isArray(arr)) {
          setOpcionesObligaciones(["OTRA (especificar)"]);
          return;
        }

        const lista = arr
          .map(
            (item) =>
              item?.descripcion ||
              item?.obligacion ||
              item?.nombre ||
              item?.titulo ||
              item?.observacion ||
              ""
          )
          .map((s) => String(s).trim())
          .filter(Boolean);

        const unicasOrdenadas = Array.from(new Set(lista)).sort((a, b) =>
          a.localeCompare(b, "es")
        );

        setOpcionesObligaciones([...unicasOrdenadas, "OTRA (especificar)"]);
      } catch (error) {
        console.error("❌ Error al obtener obligaciones:", error);
        setOpcionesObligaciones(["OTRA (especificar)"]);
        Swal.fire("Error", "No se pudieron cargar las obligaciones.", "error");
      } finally {
        setLoadingObligaciones(false);
      }
    };

    obtenerObligaciones();
  }, [contratoId]);

  const cleanData = () => {
    setActiveTab("info");
    setDireccionGeneralRegional("");
    setDependenciaCentro("");
    setCiudadDepartamentoMunicipio("");
    setContactoNombre("");
    setContactoEmail("");
    setRuta("");
    setEntidad("");
    setFechaInicioDesplazamiento("");
    setFechaFinDesplazamiento("");
    setObjetivoDesplazamiento("");
    setGastoAereo("");
    setGastoTerrestre("");
    setGastoIntermunicipal("");
    setAgendaDias([{ ...blankDia }]);
    setObligacionesDin([{ ...blankObligacion }]);
  };

  const handleAddDia = () => {
    if (agendaDias.length >= 5) {
      Swal.fire("Límite", "Máximo 5 días por agenda.", "info");
      return;
    }

    setAgendaDias((prev) => [...prev, { ...blankDia }]);
  };

  const handleRemoveDia = (index) => {
    setAgendaDias((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [{ ...blankDia }];
    });
  };

  const handleDiaChange = (index, field, value) => {
    setAgendaDias((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddActividad = (diaIndex) => {
    setAgendaDias((prev) => {
      const updated = [...prev];
      const dia = updated[diaIndex];

      const acts = Array.isArray(dia.actividades) ? [...dia.actividades] : [];

      if (acts.length >= 10) return prev;

      const nueva = { horaInicio: "", horaFin: "", actividad: "" };

      if (acts.length > 0 && acts[acts.length - 1]?.horaFin) {
        nueva.horaInicio = acts[acts.length - 1].horaFin;
      }

      acts.push(nueva);
      updated[diaIndex] = { ...dia, actividades: acts };

      return updated;
    });
  };

  const handleActividadChange = (diaIndex, actIndex, field, value) => {
    setAgendaDias((prev) => {
      const updated = [...prev];
      const dia = updated[diaIndex];
      const acts = [...(dia.actividades || [])];

      acts[actIndex] = { ...(acts[actIndex] || {}), [field]: value };

      if (field === "horaFin") {
        const siguiente = acts[actIndex + 1];
        if (siguiente) acts[actIndex + 1] = { ...siguiente, horaInicio: value };
      }

      updated[diaIndex] = { ...dia, actividades: acts };

      return updated;
    });
  };

  const handleRemoveActividad = (diaIndex, actIndex) => {
    setAgendaDias((prev) => {
      const updated = [...prev];
      const dia = updated[diaIndex];

      let acts = (dia.actividades || []).filter((_, i) => i !== actIndex);

      if (acts.length === 0) acts = [{ ...blankActividad }];

      updated[diaIndex] = { ...dia, actividades: acts };

      return updated;
    });
  };

  const isObligacionSeleccionada = useCallback(
    (texto) => (obligacionesDin || []).some((o) => o?.obligacion === texto),
    [obligacionesDin]
  );

  const quitarSeleccionada = useCallback(
    (texto) => {
      setObligacionesDin((prev) => {
        const next = (prev || []).filter((o) => o?.obligacion !== texto);
        return next.length ? next : [{ ...blankObligacion }];
      });
    },
    [blankObligacion]
  );

  const toggleObligacionCatalogo = useCallback(
    (texto) => {
      if (!texto) return;

      setObligacionesDin((prev) => {
        const arr = Array.isArray(prev) ? [...prev] : [];
        const existe = arr.some((o) => o?.obligacion === texto);
        const esSoloBlank = arr.length === 1 && !arr[0]?.obligacion;

        if (existe) {
          const next = arr.filter((o) => o?.obligacion !== texto);
          return next.length ? next : [{ ...blankObligacion }];
        }

        const nuevo = { obligacion: texto, detalle: "" };

        if (esSoloBlank) return [nuevo];

        if (arr.length >= 15) {
          Swal.fire("Límite", "Máximo 15 obligaciones.", "info");
          return arr;
        }

        return [...arr, nuevo];
      });
    },
    [blankObligacion]
  );

  const setDetalleOtra = useCallback((value) => {
    setObligacionesDin((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      const idx = arr.findIndex((o) => o?.obligacion === "OTRA (especificar)");

      if (idx === -1) {
        const nuevo = { obligacion: "OTRA (especificar)", detalle: value };
        const esSoloBlank = arr.length === 1 && !arr[0]?.obligacion;
        return esSoloBlank ? [nuevo] : [...arr, nuevo];
      }

      arr[idx] = { ...arr[idx], detalle: value };

      return arr;
    });
  }, []);

  const buildAgendaPayload = () => {
    const contactoEntidad = (() => {
      const n = String(contactoNombre || "").trim();
      const em = String(contactoEmail || "").trim();

      if (n && em) return `${n} - ${em}`;

      return n || em || "N/A";
    })();

    const obligacionesContractuales = (obligacionesDin || [])
      .filter((o) => String(o?.obligacion || "").trim())
      .map((o) => {
        if (o.obligacion === "OTRA (especificar)") {
          const det = String(o?.detalle || "").trim();
          return { obligacion: det ? `OTRA: ${det}` : "OTRA" };
        }

        return { obligacion: String(o.obligacion).trim() };
      });

    const agendaDiasLimpia = (agendaDias || []).map((dia) => ({
      fecha: dia.fecha,
      pernocta: !!dia.pernocta,
      rutaIda: String(dia.rutaIda || "").trim(),
      transporteIda: String(dia.transporteIda || "").trim(),
      rutaRegreso: String(dia.rutaRegreso || "").trim(),
      transporteRegreso: String(dia.transporteRegreso || "").trim(),
      observaciones: String(dia.observaciones || "").trim(),
      actividades: (dia.actividades || []).map((a) => ({
        horaInicio: String(a.horaInicio || "").trim(),
        horaFin: String(a.horaFin || "").trim(),
        actividad: String(a.actividad || "").trim(),
      })),
    }));

    return {
      contrato: contratoId,
      ruta: ruta.trim(),
      entidad: entidad.trim(),
      ciudadDepartamentoMunicipio: ciudadDepartamentoMunicipio.trim(),
      contactoEntidad,
      fechaInicioDesplazamiento,
      fechaFinDesplazamiento,
      objetivoDesplazamiento: objetivoDesplazamiento.trim(),
      obligacionesContractuales,
      agendaDias: agendaDiasLimpia,
      gastosaereo: Number(gastoAereo || 0),
      gastosterrestre: Number(gastoTerrestre || 0),
      gastosintermunicipal: Number(gastoIntermunicipal || 0),
    };
  };

  const saveAgenda = async () => {
    if (!puedeCrear) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para crear agendas de viaje.",
        "warning"
      );
      return;
    }

    try {
      setLoading(true);

      const payload = buildAgendaPayload();
      const res = await axios.post(
        "/api/agendasviaticos/add",
        payload,
        authConfig()
      );

      if (!res.data?.ok) {
        Swal.fire(
          "Error",
          res.data?.message || "No se pudo guardar la agenda.",
          "error"
        );
        return;
      }

      await Swal.fire({
        icon: "success",
        title: "Agenda guardada",
        text: "La información se almacenó correctamente.",
        timer: 1500,
        showConfirmButton: false,
      });

      navigate(-1);
    } catch (error) {
      console.error("❌ saveAgenda error:", error);

      const msg =
        error?.response?.data?.message || "No se pudo guardar la agenda.";
      const detalles = error?.response?.data?.detalles;

      Swal.fire({
        icon: "error",
        title: "Error",
        html: Array.isArray(detalles)
          ? `<div style="text-align:left"><b>${msg}</b><ul>${detalles
            .map((d) => `<li>${d}</li>`)
            .join("")}</ul></div>`
          : msg,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateAgenda = async () => {
    if (!puedeActualizar) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para actualizar agendas de viaje.",
        "warning"
      );
      return;
    }

    try {
      if (!agendaId) {
        Swal.fire("Error", "No se encontró la agenda a actualizar.", "error");
        return;
      }

      setLoading(true);

      const payload = buildAgendaPayload();
      const res = await axios.put(
        `/api/agendasviaticos/update/${agendaId}`,
        payload,
        authConfig()
      );

      if (!res.data?.ok) {
        Swal.fire(
          "Error",
          res.data?.message || "No se pudo actualizar la agenda.",
          "error"
        );
        return;
      }

      await Swal.fire({
        icon: "success",
        title: "Agenda actualizada",
        text: "La información se actualizó correctamente.",
        timer: 1500,
        showConfirmButton: false,
      });

      navigate(-1);
    } catch (error) {
      console.error("❌ updateAgenda error:", error);

      const msg =
        error?.response?.data?.message || "No se pudo actualizar la agenda.";
      const detalles = error?.response?.data?.detalles;

      Swal.fire({
        icon: "error",
        title: "Error",
        html: Array.isArray(detalles)
          ? `<div style="text-align:left"><b>${msg}</b><ul>${detalles
            .map((d) => `<li>${d}</li>`)
            .join("")}</ul></div>`
          : msg,
      });
    } finally {
      setLoading(false);
    }
  };

  const actions = async (e) => {
  e.preventDefault();

  const errores = [
    ...validarInfo(),
    ...validarAgenda(),
    ...validarGastos(),
  ];

  if (errores.length > 0) {
    mostrarErrores(errores);
    return;
  }

  if (!tienePermisoFormulario) {
    Swal.fire(
      "Sin permisos",
      isEditMode
        ? "No tiene permisos para actualizar agendas de viaje."
        : "No tiene permisos para crear agendas de viaje.",
      "warning"
    );
    return;
  }

  agendaId ? await updateAgenda() : await saveAgenda();
};

  if (isEditMode && !puedeActualizar) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          No tiene permisos para actualizar agendas de viaje.
        </div>

        <button className="btn btn-dark" onClick={() => navigate(-1)}>
          Volver
        </button>
      </div>
    );
  }

  if (isNewMode && !puedeCrear) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          No tiene permisos para crear agendas de viaje.
        </div>

        <button className="btn btn-dark" onClick={() => navigate(-1)}>
          Volver
        </button>
      </div>
    );
  }

  const isEmpty = (value) => String(value || "").trim() === "";

const validarInfo = () => {
  const errores = [];

  if (isEmpty(ciudadDepartamentoMunicipio)) errores.push("Ciudad / Municipio / Departamento");
  if (isEmpty(contactoNombre)) errores.push("Contacto");
  if (isEmpty(ruta)) errores.push("Ruta");
  if (isEmpty(entidad)) errores.push("Entidad o Empresa");
  if (isEmpty(fechaInicioDesplazamiento)) errores.push("Fecha inicio del desplazamiento");
  if (isEmpty(fechaFinDesplazamiento)) errores.push("Fecha fin del desplazamiento");
  if (isEmpty(objetivoDesplazamiento)) errores.push("Objetivo del desplazamiento");

  const obligacionesValidas = obligacionesDin.filter(
    (o) => !isEmpty(o.obligacion)
  );

  if (obligacionesValidas.length === 0) {
    errores.push("Obligaciones Contractuales");
  }

  const otra = obligacionesDin.find(
    (o) => o.obligacion === "OTRA (especificar)"
  );

  if (otra && isEmpty(otra.detalle)) {
    errores.push("Detalle de OTRA obligación");
  }

  return errores;
};

const validarAgenda = () => {
  const errores = [];

  agendaDias.forEach((dia, index) => {
    const numeroDia = index + 1;

    if (isEmpty(dia.fecha)) errores.push(`Día ${numeroDia}: Fecha`);
    if (isEmpty(dia.rutaIda)) errores.push(`Día ${numeroDia}: Ruta de ida`);
    if (isEmpty(dia.transporteIda)) errores.push(`Día ${numeroDia}: Transporte ida`);
    if (isEmpty(dia.rutaRegreso)) errores.push(`Día ${numeroDia}: Ruta de regreso`);
    if (isEmpty(dia.transporteRegreso)) errores.push(`Día ${numeroDia}: Transporte regreso`);

    dia.actividades.forEach((act, actIndex) => {
      const numeroActividad = actIndex + 1;

      if (isEmpty(act.horaInicio)) {
        errores.push(`Día ${numeroDia}, actividad ${numeroActividad}: Hora inicio`);
      }

      if (isEmpty(act.horaFin)) {
        errores.push(`Día ${numeroDia}, actividad ${numeroActividad}: Hora fin`);
      }

      if (isEmpty(act.actividad)) {
        errores.push(`Día ${numeroDia}, actividad ${numeroActividad}: Actividad`);
      }
    });
  });

  return errores;
};

const validarGastos = () => {
  const errores = [];

  if (isEmpty(gastoAereo)) errores.push("Gasto Aéreo");
  if (isEmpty(gastoTerrestre)) errores.push("Gasto Terrestre");
  if (isEmpty(gastoIntermunicipal)) errores.push("Intermunicipal");

  return errores;
};

const mostrarErrores = (errores) => {
  Swal.fire({
    icon: "warning",
    title: "Campos obligatorios pendientes",
    html: `
      <div style="text-align:left">
        <p>Debes diligenciar los siguientes campos:</p>
        <ul>
          ${errores.map((error) => `<li>${error}</li>`).join("")}
        </ul>
      </div>
    `,
  });
};

const cambiarPestana = (tabDestino) => {
  if (tabDestino === "agenda") {
    const errores = validarInfo();

    if (errores.length > 0) {
      mostrarErrores(errores);
      return;
    }
  }

  if (tabDestino === "gastos") {
    const erroresInfo = validarInfo();
    const erroresAgenda = validarAgenda();
    const errores = [...erroresInfo, ...erroresAgenda];

    if (errores.length > 0) {
      mostrarErrores(errores);
      return;
    }
  }

  setActiveTab(tabDestino);
};

  return (
    <div>
      <div className="container-flex">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {agendaId
                    ? "Editar Agenda de Viáticos"
                    : "Nueva Agenda de Viáticos"}
                </h5>
                <small className="text-secondary">
                  {agendaId
                    ? "Se carga la agenda según el id y se actualiza su información."
                    : "Se carga el contrato según el id y se registra una nueva agenda."}
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>Volver
                </button>

                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={cleanData}
                  disabled={loading}
                >
                  <i className="fa-solid fa-eraser me-2"></i>Limpiar
                </button>
              </div>
            </div>

            <div className="mt-3">
              <div className="row g-2">
                <div className="col-12 col-md-auto">
                  <button
                    type="button"
                    className={`btn btn-sm w-100 ${activeTab === "info" ? "btn-dark" : "btn-outline-dark"
                      }`}
                    onClick={() => setActiveTab("info")}
                    disabled={loading}
                  >
                    <i className="fa-solid fa-circle-info me-2"></i>
                    Información
                  </button>
                </div>

                <div className="col-12 col-md-auto">
                  <button
                    type="button"
                    className={`btn btn-sm w-100 ${activeTab === "agenda" ? "btn-dark" : "btn-outline-dark"
                      }`}
                    onClick={() => cambiarPestana("agenda")}
                    disabled={loading}
                  >
                    <i className="fa-solid fa-calendar-days me-2"></i>
                    Agenda
                    <span className="badge text-bg-secondary ms-2">
                      {agendaDias.length}/5
                    </span>
                  </button>
                </div>

                <div className="col-12 col-md-auto">
                  <button
                    type="button"
                    className={`btn btn-sm w-100 ${activeTab === "gastos" ? "btn-dark" : "btn-outline-dark"
                      }`}
                    onClick={() => setActiveTab("gastos")}
                    disabled={loading}
                  >
                    <i className="fa-solid fa-coins me-2"></i>
                    Gastos
                  </button>
                </div>

                <div className="col-12 col-md">
                  <div className="h-100 d-flex align-items-center">
                    <div className="progress w-100" style={{ height: 8 }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${stepProgress}%` }}
                        aria-valuenow={stepProgress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading && (
              <div className="text-center text-secondary py-3">
                <div className="spinner-border" role="status" />
                <div className="mt-2">Procesando…</div>
              </div>
            )}

            <form
              id="form-viaticos"
              onSubmit={actions}
              className="needs-validation"
              noValidate
            >
              {activeTab === "info" && (
                <div>
                  <div className="px-3 px-md-4 py-3 border-bottom">
                    <div className="row g-2 align-items-start">
                      <div className="col-12 col-md">
                        <div className="fw-semibold">
                          <i className="fa-regular fa-file-lines me-2"></i>
                          Información general
                        </div>

                        <div className="small text-secondary">
                          Se muestra el formato y debajo los campos editables.
                        </div>
                      </div>

                      <div className="col-12 col-md-auto">
                        <div className="d-flex flex-wrap gap-2 justify-content-start justify-content-md-end">
                          <span className="badge text-bg-dark">
                            <i className="fa-solid fa-file-contract me-2"></i>
                            {contratoNumero || "—"}
                          </span>

                          <span className="badge text-bg-light border">
                            <i className="fa-regular fa-calendar-check me-2"></i>
                            Vence: <b>{fechaVencimientoContrato || "—"}</b>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 px-md-4 pb-4">
                    <div>
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                        <div className="fw-semibold">
                          <i className="fa-solid fa-id-card me-2"></i>
                          Contratista y contrato
                        </div>

                        <span className="badge text-bg-light border text-wrap">
                          <i className="fa-solid fa-user me-2"></i>
                          {contratistaNombre || "—"}
                        </span>
                      </div>

                      <div className="row g-3">
                        <div className="col-12 col-lg-6">
                          <div className="bg-white border rounded-4 p-3 p-md-4 h-100">
                            <div className="small text-secondary">
                              Nombre completo
                            </div>

                            <div className="fw-semibold text-uppercase text-break">
                              {contratistaNombre || "—"}
                            </div>

                            <div className="row g-3 mt-1">
                              <div className="col-12 col-md-4">
                                <div className="small text-secondary">Tipo</div>
                                <div className="fw-semibold">
                                  {contrato?.tipoIdentificacion || "C.C."}
                                </div>
                              </div>

                              <div className="col-12 col-md-8">
                                <div className="small text-secondary">
                                  Documento
                                </div>
                                <div className="fw-semibold text-break">
                                  {contratistaDocumento || "—"}
                                </div>
                              </div>
                            </div>

                            <hr className="my-3" />

                            <div className="row g-2">
                              <div className="col-12 col-md-6">
                                <div className="small text-secondary">
                                  Supervisor
                                </div>
                                <div className="fw-semibold text-break">
                                  {supervisorNombre}
                                </div>
                                <div className="small text-secondary">
                                  {supervisorCargo}
                                </div>
                              </div>

                              <div className="col-12 col-md-6">
                                <div className="small text-secondary">
                                  Ordenador del gasto
                                </div>
                                <div className="fw-semibold text-break">
                                  {ordenadorNombre}
                                </div>
                                <div className="small text-secondary">
                                  {ordenadorCargo}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-12 col-lg-6">
                          <div className="bg-white border rounded-4 p-3 p-md-4 h-100">
                            <div className="row g-3">
                              <div className="col-12 col-md-6">
                                <div className="small text-secondary">
                                  Número
                                </div>
                                <div className="fw-semibold text-uppercase text-break">
                                  {contratoNumero || "—"}
                                </div>
                              </div>

                              <div className="col-12 col-md-6">
                                <div className="small text-secondary">
                                  Vencimiento
                                </div>
                                <div className="fw-semibold text-uppercase">
                                  {fechaVencimientoContrato || "—"}
                                </div>
                              </div>

                              <div className="col-12">
                                <div className="small text-secondary">
                                  Objeto contractual
                                </div>
                                <div className="text-uppercase text-break">
                                  {contratoObjeto || "—"}
                                </div>
                              </div>

                              <div className="col-12">
                                <div className="small text-secondary">
                                  Regional / Centro
                                </div>
                                <div className="fw-semibold text-break">
                                  {direccionGeneralRegional || "—"} /{" "}
                                  {dependenciaCentro || "—"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <hr className="my-4" />

                    <div className="border rounded-4 p-3 p-md-4 bg-body-tertiary mb-4">
                      <div className="row g-3">
                        <div className="col-12 col-md-6">
                          <label className="form-label fw-semibold">
                            Ciudad / Municipio / Departamento *
                          </label>
                          <input
                            type="text"
                            className="form-control border"
                            value={ciudadDepartamentoMunicipio}
                            onChange={(e) =>
                              setCiudadDepartamentoMunicipio(e.target.value)
                            }
                            placeholder="Ej: El Carmen de Viboral"
                            required
                          />
                        </div>

                        <div className="col-12 col-md-6">
                          <label className="form-label fw-semibold">
                            Contacto (Nombre) *
                          </label>
                          <input
                            type="text"
                            className="form-control border"
                            value={contactoNombre}
                            onChange={(e) => setContactoNombre(e.target.value)}
                            placeholder="Ej: Isabel Cristina Trujillo"
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          Ruta *
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-white">
                            <i className="fa-solid fa-route"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            value={ruta}
                            onChange={(e) => setRuta(e.target.value)}
                            placeholder="Ej: Medellín → Rionegro → Medellín"
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          Entidad o Empresa *
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-white">
                            <i className="fa-solid fa-building"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            value={entidad}
                            onChange={(e) => setEntidad(e.target.value)}
                            placeholder="Ej: Recinto Quirama"
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          Fecha inicio del desplazamiento *
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-white">
                            <i className="fa-regular fa-calendar"></i>
                          </span>
                          <input
                            type="date"
                            className="form-control"
                            value={fechaInicioDesplazamiento}
                            onChange={(e) =>
                              setFechaInicioDesplazamiento(e.target.value)
                            }
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          Fecha fin del desplazamiento *
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-white">
                            <i className="fa-regular fa-calendar-check"></i>
                          </span>
                          <input
                            type="date"
                            className="form-control"
                            value={fechaFinDesplazamiento}
                            onChange={(e) =>
                              setFechaFinDesplazamiento(e.target.value)
                            }
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="col-12">
                        <label className="form-label fw-semibold">
                          Objetivo del desplazamiento *
                        </label>
                        <textarea
                          className="form-control"
                          rows="4"
                          value={objetivoDesplazamiento}
                          onChange={(e) =>
                            setObjetivoDesplazamiento(e.target.value)
                          }
                          placeholder="Describe el objetivo del desplazamiento..."
                          required
                          disabled={loading}
                        />
                      </div>

                      <div className="col-12 pt-2">
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-2 mb-3">
                          <div>
                            <label className="form-label fw-semibold mb-0">
                              Obligaciones Contractuales{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <div className="small text-secondary">
                              Marca una o varias obligaciones. Si eliges “OTRA
                              (especificar)”, escribe el detalle.
                            </div>
                          </div>

                          <div className="d-flex gap-2 flex-wrap">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-dark"
                              disabled={
                                loading ||
                                loadingObligaciones ||
                                (opcionesObligaciones || []).length === 0
                              }
                              onClick={() => {
                                const todas = (
                                  opcionesObligaciones || []
                                ).filter((x) => x !== "OTRA (especificar)");

                                setObligacionesDin((prev) => {
                                  const ya = new Set(
                                    (prev || []).map((o) => o?.obligacion)
                                  );

                                  const nuevas = todas
                                    .filter((t) => !ya.has(t))
                                    .map((t) => ({
                                      obligacion: t,
                                      detalle: "",
                                    }));

                                  const esSoloBlank =
                                    (prev || []).length === 1 &&
                                    !(prev || [])[0]?.obligacion;

                                  const base = esSoloBlank ? [] : prev || [];
                                  const merged = [...base, ...nuevas];

                                  if (merged.length > 15) {
                                    Swal.fire(
                                      "Límite",
                                      "Máximo 15 obligaciones.",
                                      "info"
                                    );
                                    return merged.slice(0, 15);
                                  }

                                  return merged.length
                                    ? merged
                                    : [{ ...blankObligacion }];
                                });
                              }}
                              title="Seleccionar todas"
                            >
                              <i className="fa-solid fa-check-double me-2"></i>
                              Todas
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              disabled={
                                loading ||
                                loadingObligaciones ||
                                !Array.isArray(obligacionesDin) ||
                                obligacionesDin.length === 0
                              }
                              onClick={() =>
                                setObligacionesDin([{ ...blankObligacion }])
                              }
                              title="Limpiar selección"
                            >
                              <i className="fa-solid fa-broom me-2"></i>
                              Limpiar
                            </button>
                          </div>
                        </div>

                        {loadingObligaciones && (
                          <div className="alert alert-light border py-2 mb-3 d-flex align-items-center gap-2">
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            <span className="small text-secondary mb-0">
                              Cargando obligaciones del contrato…
                            </span>
                          </div>
                        )}

                        <div className="border rounded-4 p-3 bg-body-tertiary">
                          <div className="row g-3">
                            {(opcionesObligaciones || []).map((opt) => {
                              const checked = isObligacionSeleccionada(opt);
                              const inputId = `obl-card-${opt}`;

                              return (
                                <div
                                  key={opt}
                                  className="col-12 col-md-6 col-lg-4"
                                >
                                  <div
                                    className={`card h-100 ${checked ? "border-primary" : "border"
                                      }`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => {
                                      if (loading || loadingObligaciones)
                                        return;
                                      toggleObligacionCatalogo(opt);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        if (loading || loadingObligaciones)
                                          return;
                                        toggleObligacionCatalogo(opt);
                                      }
                                    }}
                                  >
                                    <div className="card-body d-flex gap-3">
                                      <div className="pt-1">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          id={inputId}
                                          checked={checked}
                                          disabled={
                                            loading || loadingObligaciones
                                          }
                                          onChange={() =>
                                            toggleObligacionCatalogo(opt)
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </div>

                                      <div className="flex-grow-1">
                                        <div className="d-flex justify-content-between align-items-start gap-2">
                                          <label
                                            className="form-check-label fw-semibold mb-1"
                                            htmlFor={inputId}
                                          >
                                            {opt === "OTRA (especificar)"
                                              ? "OTRA (especificar)"
                                              : "Obligación"}
                                          </label>

                                          {checked && (
                                            <span className="badge text-bg-primary">
                                              <i className="fa-solid fa-check me-1"></i>{" "}
                                              Seleccionada
                                            </span>
                                          )}
                                        </div>

                                        <div className="small text-secondary text-break mb-0">
                                          {opt}
                                        </div>

                                        {opt === "OTRA (especificar)" &&
                                          checked && (
                                            <div
                                              className="mt-3"
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                            >
                                              <label className="form-label fw-semibold mb-1">
                                                Detalle *
                                              </label>
                                              <input
                                                className="form-control"
                                                value={
                                                  obligacionesDin.find(
                                                    (o) =>
                                                      o.obligacion ===
                                                      "OTRA (especificar)"
                                                  )?.detalle || ""
                                                }
                                                onChange={(e) =>
                                                  setDetalleOtra(e.target.value)
                                                }
                                                placeholder="Especifique la obligación..."
                                                disabled={
                                                  loading ||
                                                  loadingObligaciones
                                                }
                                                required
                                              />
                                              <div className="form-text">
                                                Es obligatorio completar el
                                                detalle cuando seleccionas
                                                “OTRA”.
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    </div>

                                    <div className="card-footer bg-white d-flex justify-content-between align-items-center">
                                      <small className="text-secondary">
                                        <i className="fa-regular fa-circle-check me-2"></i>
                                        Marcar / desmarcar
                                      </small>

                                      {checked && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-danger"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            quitarSeleccionada(opt);
                                          }}
                                          disabled={
                                            loading || loadingObligaciones
                                          }
                                          title="Quitar"
                                        >
                                          <i className="fa-solid fa-xmark"></i>
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        <div className="d-flex justify-content-end mt-3">
                          <button
                            type="button"
                            className="btn btn-dark"
                            onClick={() => setActiveTab("agenda")}
                            disabled={loading}
                          >
                            Continuar{" "}
                            <i className="fa-solid fa-arrow-right ms-2"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "agenda" && (
                <div>
                  <div className="card-header bg-white border-0 pb-0">
                    <div className="row g-2 align-items-start">
                      <div className="col-12 col-md">
                        <h6 className="fw-semibold mb-0">Agenda detallada</h6>
                        <small className="text-secondary">
                          Día, ruta ida/regreso, transporte y actividades con
                          horas.
                        </small>
                      </div>

                      <div className="col-12 col-md-auto">
                        <div className="d-flex flex-column flex-md-row gap-2 justify-content-md-end">
                          <button
                            type="button"
                            className="btn btn-primary w-100 w-md-auto"
                            onClick={handleAddDia}
                            disabled={loading || agendaDias.length >= 5}
                          >
                            <i className="fa-solid fa-calendar-plus me-2"></i>
                            Agregar día
                          </button>

                          <button
                            type="button"
                            className="btn btn-outline-dark w-100 w-md-auto"
                            onClick={() => setActiveTab("gastos")}
                            disabled={loading}
                          >
                            Gastos{" "}
                            <i className="fa-solid fa-arrow-right ms-2"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card-body pt-3">
                    {agendaDias.map((dia, index) => {
                      const actsCount = Array.isArray(dia.actividades)
                        ? dia.actividades.length
                        : 0;

                      return (
                        <div
                          key={`dia-${index}`}
                          className="border rounded-4 p-3 p-md-4 mb-4"
                        >
                          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <span className="badge text-bg-dark rounded-pill">
                                <i className="fa-regular fa-calendar me-2"></i>
                                Día {index + 1}
                              </span>

                              <span className="badge text-bg-light border rounded-pill">
                                <i className="fa-regular fa-clock me-2"></i>
                                {actsCount} actividad
                                {actsCount === 1 ? "" : "es"}
                              </span>
                            </div>

                            <div className="d-flex flex-column flex-md-row gap-2 align-items-stretch align-items-md-center justify-content-md-end">
                              <select
                                className="form-select"
                                value={dia.pernocta ? "Sí" : "No"}
                                onChange={(e) =>
                                  handleDiaChange(
                                    index,
                                    "pernocta",
                                    e.target.value === "Sí"
                                  )
                                }
                                disabled={loading}
                                title="Pernocta"
                              >
                                <option value="No">Pernocta: No</option>
                                <option value="Sí">Pernocta: Sí</option>
                              </select>

                              {agendaDias.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger w-100 w-md-auto"
                                  onClick={() => handleRemoveDia(index)}
                                  disabled={loading}
                                >
                                  <i className="fa-solid fa-trash me-2"></i>
                                  Eliminar
                                </button>
                              )}
                            </div>
                          </div>

                          <hr className="my-3" />

                          <div className="row g-3">
                            <div className="col-12 col-md-3">
                              <label className="form-label fw-semibold">
                                Fecha *
                              </label>
                              <input
                                type="date"
                                className="form-control"
                                value={dia.fecha}
                                onChange={(e) =>
                                  handleDiaChange(
                                    index,
                                    "fecha",
                                    e.target.value
                                  )
                                }
                                required
                                disabled={loading}
                              />
                            </div>

                            <div className="col-12 col-md-5">
                              <label className="form-label fw-semibold">
                                Ruta de ida *
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={dia.rutaIda}
                                onChange={(e) =>
                                  handleDiaChange(
                                    index,
                                    "rutaIda",
                                    e.target.value
                                  )
                                }
                                required
                                disabled={loading}
                              />
                            </div>

                            <div className="col-12 col-md-4">
                              <label className="form-label fw-semibold">
                                Transporte (ida) *
                              </label>

                              <select
                                className="form-select"
                                value={dia.transporteIda}
                                onChange={(e) =>
                                  handleDiaChange(
                                    index,
                                    "transporteIda",
                                    e.target.value
                                  )
                                }
                                required
                                disabled={loading}
                              >
                                <option value="">Seleccione...</option>
                                <option value="TERRESTRE">TERRESTRE</option>
                                <option value="AEREO">AÉREO</option>
                              </select>
                            </div>

                            <div className="col-12 col-md-5">
                              <label className="form-label fw-semibold">
                                Ruta de regreso *
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={dia.rutaRegreso}
                                onChange={(e) =>
                                  handleDiaChange(
                                    index,
                                    "rutaRegreso",
                                    e.target.value
                                  )
                                }
                                required
                                disabled={loading}
                              />
                            </div>

                            <div className="col-12 col-md-4">
                              <label className="form-label fw-semibold">
                                Transporte (regreso) *
                              </label>
                              <select
                                className="form-select"
                                value={dia.transporteRegreso}
                                onChange={(e) =>
                                  handleDiaChange(
                                    index,
                                    "transporteRegreso",
                                    e.target.value
                                  )
                                }
                                required
                                disabled={loading}
                              >
                                <option value="">Seleccione...</option>
                                <option value="TERRESTRE">TERRESTRE</option>
                                <option value="AEREO">AÉREO</option>
                              </select>
                            </div>
                          </div>

                          <div className="mt-4">
                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-2 mb-2">
                              <div>
                                <div className="fw-semibold">
                                  Actividades *
                                </div>
                                <div className="small text-secondary">
                                  Hora inicio, hora fin y actividad.
                                </div>
                              </div>

                              <button
                                type="button"
                                className="btn btn-sm btn-primary w-100 w-md-auto"
                                onClick={() => handleAddActividad(index)}
                                disabled={loading || actsCount >= 10}
                              >
                                <i className="fa-solid fa-plus me-2"></i>
                                Agregar actividad
                              </button>
                            </div>

                            <div className="d-none d-md-block table-responsive">
                              <table className="table table-sm align-middle mb-2">
                                <thead className="table-light">
                                  <tr>
                                    <th style={{ width: 140 }}>Hora inicio</th>
                                    <th style={{ width: 140 }}>Hora fin</th>
                                    <th>Actividad</th>
                                    <th
                                      className="text-end"
                                      style={{ width: 120 }}
                                    >
                                      Acción
                                    </th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {(dia.actividades || []).map((act, aIdx) => (
                                    <tr key={`dia-${index}-act-${aIdx}`}>
                                      <td>
                                        <input
                                          type="time"
                                          className="form-control form-control-sm"
                                          value={act.horaInicio}
                                          onChange={(e) =>
                                            handleActividadChange(
                                              index,
                                              aIdx,
                                              "horaInicio",
                                              e.target.value
                                            )
                                          }
                                          required
                                          disabled={loading}
                                        />
                                      </td>

                                      <td>
                                        <input
                                          type="time"
                                          className="form-control form-control-sm"
                                          value={act.horaFin}
                                          onChange={(e) =>
                                            handleActividadChange(
                                              index,
                                              aIdx,
                                              "horaFin",
                                              e.target.value
                                            )
                                          }
                                          required
                                          disabled={loading}
                                        />
                                      </td>

                                      <td>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={act.actividad}
                                          onChange={(e) =>
                                            handleActividadChange(
                                              index,
                                              aIdx,
                                              "actividad",
                                              e.target.value
                                            )
                                          }
                                          placeholder="Ej: Reunión / Visita técnica / Ejecución..."
                                          required
                                          disabled={loading}
                                        />
                                      </td>

                                      <td className="text-end">
                                        <button
                                          type="button"
                                          className="btn btn-outline-danger btn-sm"
                                          onClick={() =>
                                            handleRemoveActividad(index, aIdx)
                                          }
                                          disabled={loading}
                                          title="Eliminar"
                                        >
                                          <i className="fa-solid fa-trash"></i>
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            <div className="d-md-none d-flex flex-column gap-3">
                              {(dia.actividades || []).map((act, aIdx) => (
                                <div
                                  key={`mobile-${index}-act-${aIdx}`}
                                  className="border rounded-4 p-3 bg-light"
                                >
                                  <div className="row g-2">
                                    <div className="col-6">
                                      <label className="small text-secondary">
                                        Inicio
                                      </label>
                                      <input
                                        type="time"
                                        className="form-control form-control-sm"
                                        value={act.horaInicio}
                                        onChange={(e) =>
                                          handleActividadChange(
                                            index,
                                            aIdx,
                                            "horaInicio",
                                            e.target.value
                                          )
                                        }
                                        disabled={loading}
                                      />
                                    </div>

                                    <div className="col-6">
                                      <label className="small text-secondary">
                                        Fin
                                      </label>
                                      <input
                                        type="time"
                                        className="form-control form-control-sm"
                                        value={act.horaFin}
                                        onChange={(e) =>
                                          handleActividadChange(
                                            index,
                                            aIdx,
                                            "horaFin",
                                            e.target.value
                                          )
                                        }
                                        disabled={loading}
                                      />
                                    </div>

                                    <div className="col-12">
                                      <label className="small text-secondary">
                                        Actividad
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={act.actividad}
                                        onChange={(e) =>
                                          handleActividadChange(
                                            index,
                                            aIdx,
                                            "actividad",
                                            e.target.value
                                          )
                                        }
                                        disabled={loading}
                                      />
                                    </div>

                                    <div className="col-12 text-end">
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm w-100"
                                        onClick={() =>
                                          handleRemoveActividad(index, aIdx)
                                        }
                                        disabled={loading}
                                      >
                                        <i className="fa-solid fa-trash me-2"></i>
                                        Eliminar actividad
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>

                            <div className="alert alert-light border mb-0 small text-secondary mt-2">
                              <i className="fa-regular fa-circle-check me-2"></i>
                              Tip: al poner “Hora fin”, se autocompleta la “Hora
                              inicio” de la siguiente fila.
                            </div>
                          </div>

                          <div className="mt-4">
                            <label className="form-label fw-semibold">
                              Observaciones (opcional)
                            </label>
                            <textarea
                              className="form-control"
                              rows={3}
                              value={dia.observaciones}
                              onChange={(e) =>
                                handleDiaChange(
                                  index,
                                  "observaciones",
                                  e.target.value
                                )
                              }
                              placeholder="Notas / novedades / evidencias…"
                              disabled={loading}
                            />
                          </div>
                        </div>
                      );
                    })}

                    <div className="d-flex flex-column flex-md-row gap-2 justify-content-between">
                      <button
                        type="button"
                        className="btn btn-outline-dark w-100 w-md-auto"
                        onClick={() => setActiveTab("info")}
                        disabled={loading}
                      >
                        <i className="fa-solid fa-arrow-left me-2"></i>Volver a
                        información
                      </button>

                      <button
                        type="button"
                        className="btn btn-dark w-100 w-md-auto"
                        onClick={() => setActiveTab("gastos")}
                        disabled={loading}
                      >
                        Continuar a gastos{" "}
                        <i className="fa-solid fa-arrow-right ms-2"></i>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "gastos" && (
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white border-0 pb-0">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div>
                        <h6 className="fw-semibold mb-0">
                          Liquidación de gastos
                        </h6>
                        <small className="text-secondary">
                          Los valores se envían como números (COP).
                        </small>
                      </div>

                      <span className="badge text-bg-light border">
                        <i className="fa-solid fa-calculator me-2"></i>
                        Total: {formatCOP(totalGastos)}
                      </span>
                    </div>
                  </div>

                  <div className="card-body pt-3">
                    <div className="row g-3">
                      <div className="col-12 col-md-4">
                        <label className="form-label fw-semibold">
                          Gasto Aéreo (COP) *
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={gastoAereo}
                          onChange={(e) => setGastoAereo(e.target.value)}
                          min="0"
                          required
                          disabled={loading}
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <label className="form-label fw-semibold">
                          Gasto Terrestre (COP) *
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={gastoTerrestre}
                          onChange={(e) => setGastoTerrestre(e.target.value)}
                          min="0"
                          required
                          disabled={loading}
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <label className="form-label fw-semibold">
                          Intermunicipal (COP) *
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={gastoIntermunicipal}
                          onChange={(e) =>
                            setGastoIntermunicipal(e.target.value)
                          }
                          min="0"
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div className="alert alert-info mt-3 mb-0">
                      <i className="fa-solid fa-circle-info me-2"></i>
                      Si un gasto no aplica, deja el valor en <b>0</b>. Total:{" "}
                      <b>{formatCOP(totalGastos)}</b>.
                    </div>

                    <div className="d-flex flex-wrap gap-2 justify-content-between mt-4">
                      <button
                        type="button"
                        className="btn btn-outline-dark"
                        onClick={() => setActiveTab("agenda")}
                        disabled={loading}
                      >
                        <i className="fa-solid fa-arrow-left me-2"></i>Volver a
                        agenda
                      </button>

                      <div className="d-flex flex-wrap gap-2 justify-content-end">
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={cleanData}
                          disabled={loading}
                        >
                          <i className="fa-solid fa-xmark me-2"></i>Cancelar
                        </button>

                        <button
                          type="submit"
                          form="form-viaticos"
                          className="btn btn-dark"
                          disabled={loading || !tienePermisoFormulario}
                          style={{
                            opacity: tienePermisoFormulario ? 1 : 0.5,
                            cursor: tienePermisoFormulario
                              ? "pointer"
                              : "not-allowed",
                          }}
                          title={
                            tienePermisoFormulario
                              ? agendaId
                                ? "Actualizar agenda"
                                : "Guardar agenda"
                              : agendaId
                                ? "Sin permiso para actualizar"
                                : "Sin permiso para crear"
                          }
                        >
                          <i
                            className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                              } me-2`}
                          ></i>
                          {agendaId ? "Actualizar Agenda" : "Guardar Agenda"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4 text-center text-secondary small">
                Verifica la información antes de guardar. (Agenda:{" "}
                {agendaDias.length} día
                {agendaDias.length === 1 ? "" : "s"})
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgendasViaticos;